import { createConnectTransport } from "connect-web-v2";
import { createAuthFromRuntimeConfig, readRuntimeConfig } from "@fox1ck-org/auth";
import { bearer, unauthenticated } from "@fox1ck-org/auth/connect";
import { CUSTOMERS_SCOPE, createFastLogin } from "@fox1ck-org/auth/saas";

/** Catalog product id (vibe-infra catalog/products/tracker.yaml). */
export const PRODUCT = "tracker";

/**
 * Local development without Keycloak.
 *
 * `bun run dev` with VITE_DEV_AUTH=1 puts the auth core into its `stub` mode:
 * authenticated from the first tick, no redirect to the realm, no bearer
 * header. It is the SPA half of the API's DEV_AUTH_BYPASS — run both together
 * or the UI will look signed in while every RPC returns unauthenticated. What
 * the UI may do comes from the API (WorkspaceService.GetCaller), so the stub
 * user needs only an org with the tracker.
 *
 * Both halves are DEV-only. `import.meta.env.DEV` is statically false in a
 * production build, so this branch is dropped from the bundle entirely.
 */
const DEV_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === "1";

/**
 * The session — realm `customers`, the PUBLIC client `tracker` (vibe-infra
 * tofu/environments/prod/keycloak/customers-clients.tf), Authorization Code +
 * PKCE through @fox1ck-org/auth. Built once from the runtime config the API
 * emits at /runtime-config.js (WEB_KEYCLOAK_ISSUER, WEB_KEYCLOAK_CLIENT_ID,
 * WEB_CONSOLE_URL); the Vite vars only fill the gaps in local `bun run dev`.
 * `CUSTOMERS_SCOPE` asks for `organization`: the token carries the one org
 * the tracker serves (its workspace).
 */
export const runtimeConfig = readRuntimeConfig({
  fallback: import.meta.env.DEV
    ? {
        keycloakIssuer: import.meta.env.VITE_KEYCLOAK_ISSUER ?? "https://auth.igrudsky.dev/realms/customers",
        keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "tracker",
        consoleUrl: import.meta.env.VITE_CONSOLE_URL ?? "https://console.igrudsky.dev",
        env: "dev",
      }
    : undefined,
});

export const auth = createAuthFromRuntimeConfig(
  runtimeConfig,
  DEV_AUTH
    ? {
        mode: "stub",
        scope: CUSTOMERS_SCOPE,
        stubUser: {
          sub: "00000000-0000-4000-8000-00000000dev0",
          email: "dev@localhost",
          name: "Dev User",
          preferredUsername: "dev",
          claims: { organization: { dev: { id: "00000000-0000-4000-8000-0000000000de", products: [PRODUCT] } } },
        },
      }
    : { scope: CUSTOMERS_SCOPE },
);

/**
 * Zero-input sign-in for orgs with SSO (D20, contract §8): `/o/<slug>`,
 * `?org=<slug>`, `?local=1` and the org this device remembers — our staff
 * land through org «Vibe»'s IdP without typing anything. Created at module
 * load, before the first render, as the package requires.
 */
export const fastLogin = createFastLogin(auth, { consoleUrl: runtimeConfig.consoleUrl ?? "", product: PRODUCT });

/**
 * The signed-in transport to console for `<SaasProvider>` (entitlements, my
 * orgs). It must be connect v2: the saas half's clients are v2, and the
 * package's default (`createConsoleTransport`) builds on this app's v1
 * connect-web — a v2 client over a v1 transport throws before any request,
 * so entitlements never arrived. Same chain as the package's console
 * transport: bearer, renew-and-replay on 401, no extra headers (CORS).
 */
export const consoleTransport = createConnectTransport({
  baseUrl: runtimeConfig.consoleUrl ?? "",
  // The interceptors are dual-version at runtime; their types are the v1 ones
  // the type checker resolves for this app (the bridge works in Vite only).
  interceptors: [unauthenticated(auth, "renew-then-login"), bearer(auth)] as unknown as Parameters<typeof createConnectTransport>[0]["interceptors"],
});
